import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Send, Trash2, Plus, RefreshCw, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

export default function DailyReportTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ recipients: [], enabled: true });
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sendingNow, setSendingNow] = useState(false);
  const [logs, setLogs] = useState({ items: [], total: 0 });
  const [logsLoading, setLogsLoading] = useState(false);

  async function loadSettings() {
    try {
      const { data } = await api.get("/reports/daily-report/settings");
      setSettings({ recipients: data.recipients || [], enabled: !!data.enabled });
    } catch (e) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }
  async function loadPreview() {
    try {
      const { data } = await api.get("/reports/daily-report/preview");
      setPreview(data);
    } catch (e) {
      toast.error("Failed to load preview");
    }
  }
  async function loadLogs() {
    setLogsLoading(true);
    try {
      const { data } = await api.get("/reports/daily-report/logs?page=1&limit=20");
      setLogs(data);
    } finally {
      setLogsLoading(false);
    }
  }

  useEffect(() => {
    loadSettings();
    loadPreview();
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addEmail() {
    const e = newEmail.trim().toLowerCase();
    if (!isValidEmail(e)) return toast.error("Enter a valid email address");
    if (settings.recipients.includes(e)) return toast.error("Already added");
    setSettings((s) => ({ ...s, recipients: [...s.recipients, e] }));
    setNewEmail("");
  }
  function removeEmail(e) {
    setSettings((s) => ({ ...s, recipients: s.recipients.filter((x) => x !== e) }));
  }
  async function saveSettings() {
    setSaving(true);
    try {
      const { data } = await api.put("/reports/daily-report/settings", settings);
      setSettings({ recipients: data.recipients || [], enabled: !!data.enabled });
      toast.success("Recipients saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }
  async function sendNow() {
    if (!settings.recipients.length) return toast.error("Add at least one recipient first");
    setSendingNow(true);
    try {
      const { data } = await api.post("/reports/daily-report/send-now");
      if (data.status === "sent") toast.success(`Report sent to ${data.recipients?.length || 0} recipient(s)`);
      else if (data.status === "partial") toast.warning("Sent to some recipients — see logs for details");
      else if (data.status === "skipped") toast.info(data.error || "Send skipped");
      else toast.error(data.error || "Send failed");
      loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSendingNow(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="daily-report-tab">
      <div className="grid md:grid-cols-2 gap-6">
        {/* --------- Recipient management --------- */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow text-muted-foreground">Automation</div>
              <div className="font-semibold text-lg mt-1">Daily Report Recipients</div>
              <div className="text-xs text-muted-foreground mt-1">
                Sent automatically every day at <b>10:00 PM IST</b>.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Enabled</span>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(v) => setSettings((s) => ({ ...s, enabled: v }))}
                disabled={!isAdmin}
                data-testid="daily-report-enabled-switch"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Input
              placeholder="name@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addEmail()}
              disabled={!isAdmin}
              data-testid="daily-report-new-email"
            />
            <Button onClick={addEmail} disabled={!isAdmin} data-testid="daily-report-add-email">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>

          <div className="mt-4 space-y-2 max-h-64 overflow-auto">
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : settings.recipients.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No recipients yet. Add at least one email to enable the daily send.
              </div>
            ) : (
              settings.recipients.map((e) => (
                <div
                  key={e}
                  className="flex items-center justify-between border rounded-md px-3 py-2 text-sm"
                  data-testid={`daily-report-recipient-${e}`}
                >
                  <span className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground" /> {e}
                  </span>
                  {isAdmin && (
                    <button
                      onClick={() => removeEmail(e)}
                      className="text-destructive hover:opacity-80"
                      data-testid={`daily-report-remove-${e}`}
                      aria-label={`Remove ${e}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="mt-4 flex gap-2">
            <Button
              onClick={saveSettings}
              disabled={!isAdmin || saving}
              data-testid="daily-report-save"
            >
              {saving ? "Saving…" : "Save Recipients"}
            </Button>
            <Button
              variant="outline"
              onClick={sendNow}
              disabled={sendingNow}
              data-testid="daily-report-send-now"
            >
              <Send className="h-4 w-4 mr-1" />
              {sendingNow ? "Sending…" : "Send Test Now"}
            </Button>
          </div>
        </Card>

        {/* --------- Preview --------- */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow text-muted-foreground">Live preview</div>
              <div className="font-semibold text-lg mt-1">Today's Report</div>
            </div>
            <Button size="sm" variant="ghost" onClick={loadPreview} data-testid="daily-report-refresh-preview">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          {preview ? (
            <div className="border rounded-md p-4 max-h-[420px] overflow-auto text-sm">
              <MetricsGrid data={preview.data} />
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Loading preview…</div>
          )}
        </Card>
      </div>

      {/* --------- Logs --------- */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="eyebrow text-muted-foreground">Audit trail</div>
            <div className="font-semibold text-lg mt-1">Report Logs</div>
          </div>
          <Button size="sm" variant="ghost" onClick={loadLogs} data-testid="daily-report-refresh-logs">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-3">Report Date</th>
                <th className="py-2 pr-3">Scheduled</th>
                <th className="py-2 pr-3">Generated</th>
                <th className="py-2 pr-3">Trigger</th>
                <th className="py-2 pr-3">Recipients</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Error</th>
              </tr>
            </thead>
            <tbody>
              {logsLoading && (
                <tr><td colSpan={7} className="py-4 text-muted-foreground">Loading…</td></tr>
              )}
              {!logsLoading && (logs.items || []).length === 0 && (
                <tr><td colSpan={7} className="py-4 text-muted-foreground">No runs yet.</td></tr>
              )}
              {(logs.items || []).map((l) => (
                <tr key={l.id} className="border-b" data-testid={`daily-report-log-${l.id}`}>
                  <td className="py-2 pr-3 font-medium">{l.report_date}</td>
                  <td className="py-2 pr-3">{l.scheduled_time || "22:00 IST"}</td>
                  <td className="py-2 pr-3">{(l.generated_at || "").replace("T", " ").slice(0, 19)}</td>
                  <td className="py-2 pr-3 capitalize">{l.trigger}</td>
                  <td className="py-2 pr-3">{(l.recipients || []).length}</td>
                  <td className="py-2 pr-3"><StatusBadge status={l.status} /></td>
                  <td className="py-2 pr-3 text-destructive text-xs">{l.error || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    sent:     { label: "Sent",     cls: "bg-emerald-100 text-emerald-800" },
    partial:  { label: "Partial",  cls: "bg-amber-100  text-amber-800" },
    failed:   { label: "Failed",   cls: "bg-red-100    text-red-800" },
    skipped:  { label: "Skipped",  cls: "bg-slate-100  text-slate-700" },
    pending:  { label: "Pending",  cls: "bg-slate-100  text-slate-700" },
  };
  const s = map[status] || map.pending;
  return <Badge className={s.cls + " border-transparent"}>{s.label}</Badge>;
}

function MetricsGrid({ data }) {
  if (!data) return null;
  const box = "border rounded-md p-3";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className={box}>
        <div className="text-xs uppercase text-muted-foreground">New Admissions</div>
        <div className="text-2xl font-bold mt-1">{data.new_admissions.total}</div>
        <div className="text-xs mt-1">
          M: {data.new_admissions.male} · F: {data.new_admissions.female}
        </div>
      </div>
      <div className={box}>
        <div className="text-xs uppercase text-muted-foreground">Left Today</div>
        <div className="text-2xl font-bold mt-1">{data.left_today.total}</div>
        <div className="text-xs mt-1">
          M: {data.left_today.male} · F: {data.left_today.female}
        </div>
      </div>
      <div className={box}>
        <div className="text-xs uppercase text-muted-foreground">Active Residents</div>
        <div className="text-2xl font-bold mt-1">{data.active.total}</div>
        <div className="text-xs mt-1">
          M: {data.active.male} · F: {data.active.female}
        </div>
      </div>
      <div className={box}>
        <div className="text-xs uppercase text-muted-foreground">Mess Total</div>
        <div className="text-2xl font-bold mt-1">{data.mess.total.total}</div>
        <div className="text-xs mt-1">
          M: {data.mess.total.male} · F: {data.mess.total.female} · G: {data.mess.total.guests}
        </div>
      </div>
      <div className={box + " md:col-span-1"}>
        <div className="text-xs uppercase text-muted-foreground">Lunch</div>
        <div className="text-lg font-bold mt-1">{data.mess.lunch.total}</div>
        <div className="text-xs mt-1">
          M: {data.mess.lunch.male} · F: {data.mess.lunch.female} · G: {data.mess.lunch.guests}
        </div>
      </div>
      <div className={box + " md:col-span-1"}>
        <div className="text-xs uppercase text-muted-foreground">Dinner</div>
        <div className="text-lg font-bold mt-1">{data.mess.dinner.total}</div>
        <div className="text-xs mt-1">
          M: {data.mess.dinner.male} · F: {data.mess.dinner.female} · G: {data.mess.dinner.guests}
        </div>
      </div>
    </div>
  );
}
